import { useState } from "react";
import { updateObject, checkValidity } from "@utils/utility";
import cloneDeep from "lodash/cloneDeep";
import { ControlModelInterface, FormOnChange, FormOnChangeArgs, InputType } from "../../types";
import { format } from "date-fns";
import { dateFormat } from "@utils/viewUtility";
import produce from "immer";
import { MvInputInterface } from "@mv-components/types";

export const useForm = (initialValue: ControlModelInterface, extraData?: any) => {
	const [controls, setControls] = useState<ControlModelInterface>(cloneDeep(initialValue));
	const [isFormValid, setFormValid] = useState(false);
	const handleChange: FormOnChange = ({ event, value, itemKey }) => {
		setControls((controls: ControlModelInterface) => getUpdatedFormControl({ itemKey, event, value }, controls));
	};

	const handleSubmit = (controlKey = "") => {
		const { formControls, isFormValid } = validateForm(controls, controlKey, extraData);
		setControls(formControls);
		setFormValid(isFormValid);
		return isFormValid;
	};

	const handleReset = () => {
		setControls(initialValue);
	};

	const _getRequestPayload = () => {
		return getRequestPayload(controls, extraData);
	};

	return {
		handleChange,
		controls,
		handleSubmit,
		isFormValid,
		setControls,
		handleReset,
		getRequestPayload: _getRequestPayload,
	};
};

export default useForm;

export const getRequestPayload = (controls: ControlModelInterface, extraData?: any) => {
	const payload: { [key: string]: any } = {};
	Object.keys(controls).forEach((key) => {
		// TODO[A]: may need to handle disable value, check with backend team about disable file
		if (controls[key].checkFromVisibility && !controls[key].checkFromVisibility?.(controls, extraData)) return;
		payload[key] = getValue(controls[key].value, controls[key]);
	});
	return payload;

	function getValue(value: any, control: MvInputInterface) {
		if (control.elementType === InputType.DATE_TIME) {
			// TODO[A]: verify date conversion logic
			return format(new Date(value), dateFormat.formatDateTimeServer);
		} else if (control.elementType === InputType.CHECKBOX_GROUP) {
			// TODO[A]: this is specific to filter and may not work for all checkbox group
			// need format from some key of model
			return Object.keys(value).join(", ");
		}
		return value;
	}
};

export const validateForm = (controls: ControlModelInterface, controlKey?: string, extraData?: any) => {
	let isFormValid = true;
	let formControls: ControlModelInterface = { ...controls };
	Object.keys(formControls).map((key) => {
		if (controlKey && key !== controlKey) {
			return true;
		}
		if (
			formControls[key].disabled ??
			(formControls[key].checkFromVisibility &&
				formControls[key].checkFromVisibility?.(formControls, extraData) === false)
		) {
			return true;
		}
		const validData = checkValidity(
			formControls[key].value,
			formControls[key].validations?.rules,
			formControls,
			key
		);
		const validStatus = validData.isValid;

		if (!validStatus) {
			isFormValid = validStatus;
		}

		formControls = updateObject(formControls, {
			[key]: updateObject(formControls[key], {
				value: formControls[key].value,
				valid: validData,
				touched: true,
			}),
		});
		return true;
	});

	return {
		isFormValid,
		formControls,
	};
};

export const resetForm = (controls: ControlModelInterface, extraData?: any) => {
	let formControls: ControlModelInterface = { ...controls };
	Object.keys(formControls).forEach((key) => {
		if (!formControls[key].disabled) {
			formControls = updateObject(formControls, {
				[key]: updateObject(formControls[key], {
					value: "",
					touched: false,
				}),
			});
		}
	});
	return formControls;
};

export const getUpdatedFormControl = ({ itemKey, event, value }: FormOnChangeArgs, controls: ControlModelInterface) => {
	const updatedControls = {
		...controls,
		[itemKey]: getUpdatedFormFieldControl({ itemKey, event, value }, controls[itemKey], controls),
	};
	return updatedControls;
};

export const getUpdatedFormFieldControl = (
	{ itemKey, event, value }: FormOnChangeArgs,
	formFieldControl: MvInputInterface,
	controls?: ControlModelInterface
) => {
	const newValue = typeof value !== "undefined" ? value : event.target.value;
	const newFormFieldControl = produce(formFieldControl, (currentControl) => {
		currentControl.value = newValue;
		currentControl.touched = true;
	});
	return produce(newFormFieldControl, (currentControl) => {
		currentControl.valid = checkValidity(
			newValue,
			currentControl.validations?.rules,
			controls
				? {
						...controls,
						[itemKey]: newFormFieldControl,
				  }
				: {},
			itemKey
		);
	});
};
